// MINIMAL MODE: Simplified announcement management (approval workflow and targeting disabled)
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { RichTextEditor } from '../../../components/common/RichTextEditor';
import { trpc } from '../../../lib/api';
import type { Announcement } from '../../../types/announcements';

const { Title, Text } = Typography;
const { Option } = Select;

type DrawerMode = 'create' | 'edit';

const priorityLabel: Record<Announcement['priority'], string> = {
  low: '低',
  normal: '通常',
  high: '高',
  critical: '緊急',
};

// MINIMAL MODE: Simplified status (draft, scheduled, completed only)
const statusColors: Record<Announcement['status'], string> = {
  draft: 'default',
  pending: 'gold',
  approved: 'blue',
  scheduled: 'processing',
  delivering: 'blue',
  completed: 'green',
  archived: 'default',
  rejected: 'red',
};

const statusLabel: Record<Announcement['status'], string> = {
  draft: '下書き',
  pending: '承認待ち',
  approved: '承認済み',
  scheduled: '配信予定',
  delivering: '配信中',
  completed: '配信済み',
  archived: 'アーカイブ',
  rejected: '差し戻し',
};

// MINIMAL MODE: Simplified interfaces
interface AnnouncementRow extends Announcement {
  key: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAnnouncement = (value: unknown): Announcement | null => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.title !== 'string' ||
    typeof value.bodyMd !== 'string' ||
    typeof value.priority !== 'string' ||
    typeof value.status !== 'string' ||
    typeof value.locale !== 'string' ||
    typeof value.requiresAck !== 'boolean' ||
    typeof value.createdAt !== 'string' ||
    typeof value.updatedAt !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    title: value.title,
    summary: typeof value.summary === 'string' ? value.summary : null,
    bodyMd: value.bodyMd,
    priority: value.priority as Announcement['priority'],
    status: value.status as Announcement['status'],
    locale: value.locale as Announcement['locale'],
    deliverFrom: typeof value.deliverFrom === 'string' ? value.deliverFrom : null,
    deliverUntil: typeof value.deliverUntil === 'string' ? value.deliverUntil : null,
    channelFlags: null,
    requiresAck: value.requiresAck,
    metadata: null,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  };
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
};

export default function AnnouncementListPage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('create');
  const [activeAnnouncementId, setActiveAnnouncementId] = useState<string | null>(null);
  const [bodyMarkdown, setBodyMarkdown] = useState<string>('');

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const listQuery = trpc.announcementAdmin.list.useQuery({ limit: 50 });
  const detailQuery = trpc.announcementAdmin.get.useQuery(activeAnnouncementId!, {
    enabled: !!activeAnnouncementId,
  });

  const createMutation = trpc.announcementAdmin.create.useMutation();
  const updateMutation = trpc.announcementAdmin.update.useMutation();
  const publishMutation = trpc.announcementAdmin.publish.useMutation(); // MINIMAL MODE: publish instead of schedule

  useEffect(() => {
    if (form.getFieldValue('bodyMd') !== bodyMarkdown) {
      form.setFieldValue('bodyMd', bodyMarkdown);
    }
  }, [form, bodyMarkdown]);

  // MINIMAL MODE: Only draft announcements are editable
  const activeAnnouncementStatus = detailQuery.data?.announcement.status ?? null;
  const editableStatuses: Announcement['status'][] = ['draft'];
  const isEditableStatus = !activeAnnouncementStatus || editableStatuses.includes(activeAnnouncementStatus);
  const isReadOnly = drawerMode === 'edit' && !isEditableStatus;

  // MINIMAL MODE: Simplified form initialization (no targeting, no scheduling)
  useEffect(() => {
    if (drawerOpen && drawerMode === 'edit' && detailQuery.data) {
      const { announcement } = detailQuery.data;
      form.setFieldsValue({
        title: announcement.title,
        summary: announcement.summary ?? '',
        bodyMd: announcement.bodyMd,
        priority: announcement.priority,
      });
      setBodyMarkdown(announcement.bodyMd ?? '');
    }
  }, [drawerOpen, drawerMode, detailQuery.data, form]);

  const columns: ColumnsType<AnnouncementRow> = [
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      render: (value: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.summary ?? '---'}</Text>
        </Space>
      ),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: Announcement['status']) => (
        <Tag color={statusColors[status] ?? 'default'}>{statusLabel[status] ?? status}</Tag>
      ),
    },
    {
      title: '優先度',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: Announcement['priority']) => priorityLabel[priority] ?? priority,
    },
    // MINIMAL MODE: deliverFrom and channelFlags columns hidden (always in-app, immediate delivery)
    // {
    //   title: '配信開始',
    //   dataIndex: 'deliverFrom',
    //   key: 'deliverFrom',
    //   width: 180,
    //   render: (value: string | null) => (value ? dayjs(value).format('YYYY/MM/DD HH:mm') : '未設定'),
    // },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (value: string) => dayjs(value).format('YYYY/MM/DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: Announcement) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openEditDrawer(record);
          }}
        >
          {record.status === 'draft' ? '編集' : '詳細'}
        </Button>
      ),
    },
  ];

  const dataSource = useMemo<AnnouncementRow[]>(
    () => (listQuery.data ?? []).flatMap((item) => {
      const normalized = normalizeAnnouncement(item);
      return normalized ? [{ ...normalized, key: normalized.id }] : [];
    }),
    [listQuery.data]
  );

  // MINIMAL MODE: Simplified drawer operations (no targeting, no scheduling)
  const openCreateDrawer = () => {
    setDrawerMode('create');
    setDrawerOpen(true);
    setBodyMarkdown('');
    setActiveAnnouncementId(null);
    form.setFieldsValue({
      title: '',
      summary: '',
      bodyMd: '',
      priority: 'normal',
    });
  };

  const openEditDrawer = (record: Announcement) => {
    setDrawerMode('edit');
    setDrawerOpen(true);
    setBodyMarkdown(record.bodyMd ?? '');
    form.setFieldsValue({
      title: record.title,
      summary: record.summary ?? '',
      bodyMd: record.bodyMd ?? '',
      priority: record.priority,
    });
    setActiveAnnouncementId(record.id);
  };

  const handleDrawerClose = () => {
    form.resetFields();
    setBodyMarkdown('');
    setActiveAnnouncementId(null);
    setDrawerOpen(false);
  };

  // MINIMAL MODE: Simplified save handler
  const handleSaveDraft = async () => {
    try {
      await form.validateFields();
      if (drawerMode === 'edit' && activeAnnouncementId) {
        if (!isEditableStatus) {
          messageApi.warning('配信済みのお知らせは編集できません。');
          return;
        }
        await updateMutation.mutateAsync({
          id: activeAnnouncementId,
          data: {
            title: form.getFieldValue('title'),
            summary: form.getFieldValue('summary'),
            bodyMd: bodyMarkdown,
            priority: form.getFieldValue('priority'),
          },
        });
        messageApi.success('下書きを更新しました');
      } else {
        const created = await createMutation.mutateAsync({
          title: form.getFieldValue('title'),
          summary: form.getFieldValue('summary') ?? undefined,
          bodyMd: bodyMarkdown,
          priority: form.getFieldValue('priority'),
          locale: 'ja',
          channelFlags: { inApp: true },
          requiresAck: false,
        });
        setActiveAnnouncementId(created.id);
        setDrawerMode('edit');
        messageApi.success('下書きを作成しました');
      }
      await listQuery.refetch();
    } catch (error: unknown) {
      messageApi.error(getErrorMessage(error, '保存に失敗しました'));
    }
  };

  // MINIMAL MODE: Original handleSchedule function (disabled)
  // const handleSchedule = async () => { ... };

  // MINIMAL MODE: Immediate publish only
  const handlePublish = async () => {
    try {
      await form.validateFields();
      let announcementId = activeAnnouncementId;

      // Save changes if editing a draft
      if (drawerMode === 'edit' && activeAnnouncementId) {
        if (isEditableStatus) {
          await updateMutation.mutateAsync({
            id: activeAnnouncementId,
            data: {
              title: form.getFieldValue('title'),
              summary: form.getFieldValue('summary'),
              bodyMd: bodyMarkdown,
              priority: form.getFieldValue('priority'),
            },
          });
        }
      } else {
        // Create new announcement if in create mode
        const created = await createMutation.mutateAsync({
          title: form.getFieldValue('title'),
          summary: form.getFieldValue('summary') ?? undefined,
          bodyMd: bodyMarkdown,
          priority: form.getFieldValue('priority'),
          locale: 'ja',
          channelFlags: { inApp: true },
          requiresAck: false,
        });
        announcementId = created.id;
        setActiveAnnouncementId(created.id);
      }

      if (!announcementId) {
        throw new Error('お知らせIDの取得に失敗しました');
      }

      await publishMutation.mutateAsync(announcementId);
      messageApi.success('お知らせを配信しました');
      handleDrawerClose();
      await listQuery.refetch();
    } catch (error: unknown) {
      messageApi.error(getErrorMessage(error, '配信に失敗しました'));
    }
  };

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>お知らせ配信（シンプル版）</Title>
            <Text type="secondary">全ユーザーに即時配信されます</Text>
          </div>
          <Button type="primary" onClick={openCreateDrawer}>新規作成</Button>
        </Space>

        <Card variant="borderless" loading={listQuery.isLoading}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Drawer
          title={
            drawerMode === 'create'
              ? '新規お知らせ作成'
              : isReadOnly
                ? 'お知らせ詳細'
                : 'お知らせ編集'
          }
          open={drawerOpen}
          forceRender
          width={720}
          onClose={handleDrawerClose}
          destroyOnHidden
          extra={
            !isReadOnly && (
              <Space>
                <Button
                  onClick={handleSaveDraft}
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  保存
                </Button>
                <Button
                  type="primary"
                  onClick={handlePublish}
                  loading={publishMutation.isPending || createMutation.isPending || updateMutation.isPending}
                >
                  配信
                </Button>
              </Space>
            )
          }
        >
          {isReadOnly && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
              <Text type="secondary">
                このお知らせは配信済みのため、編集できません。
              </Text>
            </div>
          )}
          <Form
            layout="vertical"
            form={form}
            initialValues={{
              priority: 'normal',
            }}
          >
            <Form.Item
              name="title"
              label="タイトル"
              rules={[{ required: true, message: 'タイトルを入力してください' }, { max: 150 }]}
            >
              <Input placeholder="例: システムメンテナンスのお知らせ" maxLength={150} showCount disabled={isReadOnly} />
            </Form.Item>

            <Form.Item name="summary" label="概要" rules={[{ max: 500 }]}>
              <Input.TextArea rows={2} placeholder="サマリー (任意)" maxLength={500} showCount disabled={isReadOnly} />
            </Form.Item>

            <Form.Item name="priority" label="優先度" rules={[{ required: true }]}>
              <Select disabled={isReadOnly}>
                <Option value="low">低</Option>
                <Option value="normal">通常</Option>
                <Option value="high">高</Option>
                <Option value="critical">緊急</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="本文"
              name="bodyMd"
              rules={[{ required: true, message: '本文を入力してください' }]}
              help="書式はツールバーから指定できます。"
            >
              <RichTextEditor
                value={bodyMarkdown}
                onChange={setBodyMarkdown}
                placeholder="本文を入力..."
                disabled={isReadOnly}
                minHeight={240}
                showToolbar={!isReadOnly}
                showPreviewToggle={!isReadOnly}
              />
            </Form.Item>

            {/* MINIMAL MODE: Targeting and scheduling features disabled */}
            {/* All announcements are delivered immediately to all users via in-app channel */}
          </Form>
        </Drawer>
      </Space>
    </>
  );
}




